/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';

import { Text } from 'components/Text';
import { withNotification } from 'components/Notification';
// import { LoadingScreen } from 'components/LoadingScreen';
import { NotificationTypes, apiErrorHandler } from 'utils';

import {
  GET_TRACKING_TEMPLATES,
  GET_KLAVIYO_CONNECTION,
  GET_KLAVIYO_FLOWS_STATUS,
  GET_KLAVIYO_FLOW_SEND_COUNTS,
} from 'gql/queries';
import {
  UPDATE_TRACKING_TEMPLATE,
  UPDATE_STORE,
  SET_KLAVIYO_FLOW_STATUS,
} from 'gql/mutations';

import { useStore } from 'context/store-context';

import {
  EmailNotificationTemplates,
  KlaviyoFlowForEventType,
} from './EmailNotificationTemplates';
import { EmailTemplateSection } from './EmailTemplateSection';
import {
  defaultEmailTemplates,
  defaultTrackingTemplate,
} from './data/templates-data';
import { resolveTemplates, sortByIsTrackingDefault } from './utils';

type NotificationTemplatesProps = {
  embedded?: boolean;
};

export const NotificationTemplates = withNotification(
  ({
    showNotification,
    embedded = false,
  }: NotificationTemplatesProps & any) => {
    const store = useStore();
    const { storeId } = store;

    const [templates, setTemplates] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { data: klaviyoConnectionData } = useQuery(GET_KLAVIYO_CONNECTION, {
      variables: { storeId },
      skip: !storeId,
      fetchPolicy: 'network-only',
    });
    const isKlaviyoConnected = Boolean(
      klaviyoConnectionData?.klaviyoConnection?.connected,
    );

    const { data: klaviyoFlowsStatusData } = useQuery(
      GET_KLAVIYO_FLOWS_STATUS,
      {
        variables: { storeId },
        skip: !storeId || !isKlaviyoConnected,
        fetchPolicy: 'network-only',
      },
    );
    const klaviyoFlowStatuses: {
      flowId: string;
      name: string;
      status: string;
      eventType: string;
    }[] = klaviyoFlowsStatusData?.klaviyoFlowsStatus || [];

    const [sendCountWindow, setSendCountWindow] = useState<number>(30);
    const sendCountRange = useMemo(
      () => ({
        since: moment()
          .subtract(sendCountWindow, 'days')
          .format('YYYY-MM-DDTHH:mm:ss'),
        until: moment().format('YYYY-MM-DDTHH:mm:ss'),
      }),
      [sendCountWindow],
    );

    const { data: klaviyoSendCountsData } = useQuery(
      GET_KLAVIYO_FLOW_SEND_COUNTS,
      {
        variables: { storeId, ...sendCountRange },
        skip: !storeId || !isKlaviyoConnected || !klaviyoFlowStatuses.length,
        fetchPolicy: 'network-only',
      },
    );
    const klaviyoSendCounts: { flowId: string; sendCount: number }[] =
      klaviyoSendCountsData?.klaviyoFlowSendCounts || [];

    const klaviyoFlowsByEventType = useMemo(() => {
      const map: Record<string, KlaviyoFlowForEventType> = {};
      if (!isKlaviyoConnected) return map;

      klaviyoFlowStatuses.forEach((flow) => {
        map[flow.eventType] = {
          flowId: flow.flowId,
          status: flow.status,
          sendCount: klaviyoSendCounts.find(
            (count) => count.flowId === flow.flowId,
          )?.sendCount,
        };
      });
      return map;
    }, [isKlaviyoConnected, klaviyoFlowStatuses, klaviyoSendCounts]);

    const [togglingKlaviyoFlowId, setTogglingKlaviyoFlowId] = useState<
      string | null
    >(null);
    const [setKlaviyoFlowStatus] = useMutation(SET_KLAVIYO_FLOW_STATUS, {
      refetchQueries: ['klaviyoFlowsStatus'],
    });

    const handleToggleKlaviyoFlow = async (
      flow: KlaviyoFlowForEventType,
      turnOn: boolean,
    ) => {
      if (!storeId || togglingKlaviyoFlowId) return;
      setTogglingKlaviyoFlowId(flow.flowId);
      try {
        const { data } = await setKlaviyoFlowStatus({
          variables: {
            storeId,
            flowId: flow.flowId,
            status: turnOn ? 'live' : 'draft',
          },
        });
        if (!data?.setKlaviyoFlowStatus) {
          showNotification(
            NotificationTypes.error,
            "Klaviyo couldn't update this flow. Check its email content in Klaviyo and try again.",
          );
        }
      } catch (error: any) {
        const newError = apiErrorHandler(error);
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            "Klaviyo couldn't update this flow. Check its email content in Klaviyo and try again.",
        );
      } finally {
        setTogglingKlaviyoFlowId(null);
      }
    };

    const userDefaultTrackingTemplate =
      templates?.find((template: any) => template.isTrackingDefault) ??
      defaultTrackingTemplate;

    const [getTrackingTemplates] = useLazyQuery(GET_TRACKING_TEMPLATES, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        const hasDefaultTemplate = data?.trackingTemplates?.some(
          (template: any) => template.isTrackingDefault,
        );
        setTemplates(
          hasDefaultTemplate
            ? (data?.trackingTemplates ?? []).sort(sortByIsTrackingDefault)
            : [userDefaultTrackingTemplate, ...data?.trackingTemplates],
        );
      },
    });

    const [updateTrackingTemplate] = useMutation(UPDATE_TRACKING_TEMPLATE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        setTemplates((templates: any) =>
          resolveTemplates(templates, data.saveTrackingTemplate),
        );
        showNotification(
          NotificationTypes.success,
          'Template Updated Successfully!',
        );
      },
    });

    const [updateStore] = useMutation(UPDATE_STORE, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const resolveDefaultsTemplates = (apiTemplates: any) => {
      return defaultEmailTemplates.map((defaultTemplate) => {
        const respectiveApiTemplate = apiTemplates.find((apiTemplate: any) => {
          return (
            apiTemplate.eventType === defaultTemplate.eventType &&
            apiTemplate.usage === defaultTemplate.usage
          );
        });

        return respectiveApiTemplate ?? defaultTemplate;
      });
    };

    const resolvedEmailTemplates = resolveDefaultsTemplates(templates ?? []);

    useEffect(() => {
      const getTemplates = async () => {
        try {
          if (storeId) {
            getTrackingTemplates({
              variables: {
                storeId,
              },
            });
          }
        } catch (e) {
          setTemplates(defaultEmailTemplates);
        }
      };

      getTemplates();
    }, []);

    useEffect(() => {
      if (
        store.storeProperties &&
        !store.storeProperties.completedSteps?.hasVisitedNotificationPage
      ) {
        updateStore({
          variables: {
            storeId,
            set: {
              completedSteps: {
                ...store.storeProperties.completedSteps,
                hasVisitedNotificationPage: true,
              },
            },
          },
        });
      }
    }, [store.storeProperties, storeId, updateStore]);

    const handleChangeActiveTemplate = async (template: any) => {
      if (!template) return;
      try {
        setIsLoading(true);
        await updateTrackingTemplate({
          variables: {
            input: {
              storeId,
              id: template.id,
              active: !template.active,
              eventType: template.eventType,
              platformIntegrationType: template.platformIntegrationType,
              subject: template.subject,
              templateHtml: template.templateHtml ?? '',
              templateJson: template.templateJson ?? '',
              usage: template.usage,
            },
          },
        });
        setIsLoading(false);
      } catch (e) {
        console.log('error', e);
      } finally {
        setIsLoading(false);
      }
    };

    const renderEmailTemplates = () => {
      return (
        <EmailNotificationTemplates
          userTemplates={resolvedEmailTemplates}
          isLoading={isLoading}
          onChangeActiveTemplate={handleChangeActiveTemplate}
          embedded={embedded}
          klaviyoFlowsByEventType={klaviyoFlowsByEventType}
          onToggleKlaviyoFlow={handleToggleKlaviyoFlow}
          togglingKlaviyoFlowId={togglingKlaviyoFlowId}
        />
      );
    };

    return (
      <div className={classNames(embedded && 'space-y-6')}>
        {!embedded && (
          <div className="max-w-7xl mx-auto mb-12 px-4 sm:px-6 md:px-8 mt-12">
            <div className="flex items-center justify-between max-w-6xl">
              <div>
                <Text value="Customer Notifications" type="h1" />
              </div>
            </div>
          </div>
        )}
        {isKlaviyoConnected ? (
          <EmailTemplateSection
            storeId={storeId}
            connectionTemplateStyle={
              klaviyoConnectionData?.klaviyoConnection?.templateStyle
            }
            showNotification={showNotification}
            sendCountWindow={sendCountWindow}
            onChangeSendCountWindow={setSendCountWindow}
          />
        ) : null}
        <div
          className={classNames(
            !embedded && 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8',
          )}
        >
          {renderEmailTemplates()}
        </div>
      </div>
    );
  },
);
